import { PageAction } from "../types/paginateOptions";
import { API } from "./config";
import { AxiosResponse } from 'axios';
import { ICashflowAllDataResponse } from "../Interfaces/ICashflowAPIResponse";
import { ICashflowFilters } from "../Interfaces/ICashflowFilters";
import { ICashflowFormSave } from "../Interfaces/ICashflowFormSave";
import { ISaveCashflowForm } from "../Interfaces/ISaveCashflowForm";
import { IAllImagesResponse } from "../Interfaces/IImagesResponse";
import { CashflowStatusOptions } from "../components/CashflowStatus";
import { ICashflowData } from "../Interfaces/ICashflowData";

const buildFilterPayload = (filters: ICashflowFilters, enforceClientId?: number): Record<string, unknown> => {
  if (!filters) return {};

  const payload: Record<string, unknown> = {};

  const normalizeString = (value?: string | number | null) => {
    if (value === undefined || value === null) return undefined;
    const str = String(value).trim();
    return str.length ? str : undefined;
  };

  const pickFirstString = (...candidates: (string | number | undefined | null)[]) => {
    for (const candidate of candidates) {
      const normalized = normalizeString(candidate);
      if (normalized) return normalized;
    }
    return undefined;
  };

  const assign = (key: string, value?: unknown) => {
    if (value === undefined || value === null) return;
    if (typeof value === 'string' && value.trim() === '') return;
    payload[key] = value;
  };

  const client = pickFirstString(filters.client, filters.clientName);
  assign('client', client);

  const month = pickFirstString(filters.mes, filters.month);
  assign('mes', month);

  const year = pickFirstString(filters.ano, filters.year);
  assign('ano', year);

  const singleDate = pickFirstString(filters.data);
  assign('data', singleDate);

  const conta = pickFirstString(filters.conta, filters.invoice);
  assign('conta', conta);
  assign('invoice', pickFirstString(filters.invoice, filters.conta, conta));

  const condicao = pickFirstString(filters.condicao, filters.classification);
  assign('condicao', condicao);
  assign('classification', pickFirstString(filters.classification, filters.condicao, condicao));

  const origem = pickFirstString(filters.origem, filters.origin);
  assign('origem', origem);
  assign('origin', pickFirstString(filters.origin, filters.origem, origem));

  const historico = pickFirstString(filters.historico, filters.history);
  assign('historico', historico);
  assign('history', pickFirstString(filters.history, filters.historico, historico));

  const status = normalizeString(filters.status);
  assign('status', status);

  const numericFrom = (value?: string | number | null) => {
    if (value === undefined || value === null) return undefined;
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    const normalized = normalizeString(value);
    if (!normalized) return undefined;
    const sanitized = normalized.replace(/\s/g, '').replace(',', '.');
    const parsed = Number(sanitized);
    return Number.isFinite(parsed) ? parsed : undefined;
  };

  const valor = numericFrom(filters.valor ?? filters.value);
  if (valor !== undefined) {
    assign('valor', valor);
    assign('value', valor);
  }

  const dateStart = normalizeString(filters.dateStart);
  const dateEnd = normalizeString(filters.dateEnd);

  if (singleDate) {
    assign('dateStart', singleDate);
    assign('dateEnd', singleDate);
  } else {
    assign('dateStart', dateStart);
    assign('dateEnd', dateEnd);
  }

  const effectiveClientId = enforceClientId ?? filters.clientId;
  assign('clientId', effectiveClientId);
  assign('analystId', filters.analystId);

  return payload;
};

const postExportRequest = (filters: ICashflowFilters, token: string, enforceClientId?: number): Promise<AxiosResponse<{ data: ICashflowData[] }>> => {
  const bodyData = { filters: buildFilterPayload(filters, enforceClientId) };
  return API.post(`/commitment/export`, { ...bodyData }, { headers: { 'Authorization': `Bearer ${token}` } });
};

export const cashflowService = {
  getData: async (skip: number, action: PageAction, token: string): Promise<AxiosResponse<ICashflowAllDataResponse>> => {
    const start = action === "Initial";
    return await API.get(`/commitment/${skip}/${start}`, { headers: { 'Authorization': `Bearer ${token}` } });
  },

  getRecurrences: async (recurrenceId: string, token: string): Promise<AxiosResponse<{ data: Pick<ICashflowData, "id" | "date">[] }>> => {
    return await API.get(`/commitment/recurrence/${recurrenceId}`, { headers: { 'Authorization': `Bearer ${token}` } });
  },

  getConsumerData: async (skip: number, userID: number, action: PageAction, token: string) => {
    const start = action === "Initial";
    return await API.get(`/commitment/client/${skip}/${userID}/${start}`, { headers: { 'Authorization': `Bearer ${token}` } });
  },

  getInvoices: async (token: string): Promise<AxiosResponse<{ invoices: string[] }>> => {
    return await API.get('/parameter/find/invoices', { headers: { 'Authorization': `Bearer ${token}` } });
  },

  getFilterData: async (
    skip: number,
    action: PageAction,
    filters: ICashflowFilters,
    token: string,
    options?: { clientId?: number }
  ): Promise<AxiosResponse<ICashflowAllDataResponse>> => {
    const bodyData = {
      skip,
      start: action === "Initial",
      filters: buildFilterPayload(filters, options?.clientId)
    }
    return await API.post(`/commitment/customSearch`, { ...bodyData }, { headers: { 'Authorization': `Bearer ${token}` } });
  },

  create: async (token: string, data: ISaveCashflowForm, isRecourrence: boolean, recourrenceData?: { day: number, months: number, startOf?: string }): Promise<AxiosResponse> => {
    return await API.post("/commitment", { commitment: data, isRecourrence, recourrenceData }, { headers: { 'Authorization': `Bearer ${token}` } });
  },

  update: async (token: string, data: ISaveCashflowForm, id: string | number, isRecurrence: boolean ): Promise<AxiosResponse> => {
    return await API.put(`/commitment/${String(id)}`, { commitment: data, isRecurrence }, { headers: { 'Authorization': `Bearer ${token}` } });
  },

  uploadImage: async (token: string, dataForm: FormData) => {
    return await API.post(`/image/upload`, { dataForm }, { headers: { 'Authorization': `Bearer ${token}` } });
  },

  getImages: async (token: string): Promise<AxiosResponse<IAllImagesResponse>> => {
    return await API.get("/image", { headers: { 'Authorization': `Bearer ${token}` } });
  },

  getConsumerImages: async (token: string, consumerID: number): Promise<AxiosResponse<IAllImagesResponse>> => {
    return await API.get(`/image/${consumerID}`, { headers: { 'Authorization': `Bearer ${token}` } });
  },

  deleteCashflow: async (token: string, id: number | number[], isRecurrence: boolean): Promise<AxiosResponse> => {
    return await API.delete(`/commitment/${id}?isRecurrence=${isRecurrence}`, { headers: { 'Authorization': `Bearer ${token}` } });
  },

  changeStatus: async (token: string, id: number, status: CashflowStatusOptions, analystId: number): Promise<AxiosResponse> => {
    return await API.put(`/commitment/update/status/${id}`, { status, analystId }, { headers: { 'Authorization': `Bearer ${token}` } })
  },

  exportToExcel: async (filters: ICashflowFilters, token: string, options?: { clientId?: number }): Promise<AxiosResponse<{ data: ICashflowData[] }>> => {
    return await postExportRequest(filters, token, options?.clientId);
  },

  export: async (filters: ICashflowFilters, token: string, options?: { clientId?: number }): Promise<AxiosResponse<{ data: ICashflowData[] }>> => {
    return await postExportRequest(filters, token, options?.clientId);
  },
}
